/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RegionBaseProps {
  id: string;
  baseId: string;
  regionId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RegionBaseRecord extends Record<RegionBaseProps>({
  id: null as any,
  baseId: null as any,
  regionId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    baseId: string;
    regionId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RegionBaseRecord {
    if (json == null) {
      throw new Error('RegionBaseRecord.fromJS: parameter cannot be null');
    }
    return new RegionBaseRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RegionBaseJson.id',
      ),
      baseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
        'RegionBaseJson.baseId',
      ),
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'RegionBaseJson.regionId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RegionBaseJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    baseId: string;
    regionId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RegionBaseRecord, Error[]] {
    if (json == null) {
      throw new Error('RegionBaseRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RegionBaseRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RegionBaseJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
            'RegionBaseJson.baseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'RegionBaseJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RegionBaseJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RegionBaseProps) {
    super(props);
    if (props == null) {
      throw new Error('RegionBaseRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RegionBaseViewModelBase implements RegionBaseProps {
  protected readonly _props: RegionBaseProps;
  private _json?: RegionBaseProps;

  get id(): string {
    return this._props.id;
  }
  get baseId(): string {
    return this._props.baseId;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: RegionBaseProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        baseId: this._props.baseId,
        regionId: this._props.regionId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRegionBaseEntities = (state: AppState) =>
  state.entityStores.regionBase.entities;

export const selectRegionBaseById = (id: string) => (state: AppState) =>
  state.entityStores.regionBase.entities.get(id);

export const selectRegionBaseMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.regionBase.meta.get(id);

export const selectRegionBaseAndMetadataById = (id: string) =>
  createSelector(
    selectRegionBaseById(id),
    selectRegionBaseMetadataById(id),
    (regionBase, meta) => ({ regionBase, meta }),
  );

export function selectRegionBaseEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRegionBaseEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RegionBaseRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRegionBaseEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllRegionBaseEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectRegionBaseEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllRegionBaseEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectRegionBaseEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRegionBaseEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
